import React, { Suspense, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, fetchAuthSession, AuthSession, AuthUser, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import * as AppActionCreators from './store/actions/app.Actions';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppState } from './store/state/app.state';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import { MainLayout } from './layout';
import LoginPage from './pages/login/LoginPage';
import { toast, ToastContainer } from 'react-toastify';
import amplifyConfig from './utils/amplifyConfig';
import 'react-toastify/dist/ReactToastify.css';
import FAQPage from './pages/faq/FAQPage';
import FleetFAQPage from './pages/faq/FleetFAQPage';
import PremiumSubscriptionPage from './pages/premium-subscription/PremiumSubscriptionPage';
import { getAPIKey } from './services/login/login-service';
import { Common } from './utils/constants';
import { useTranslation } from 'react-i18next';
import { getConfigurationDetail, getProfile } from './services/user/user-service';
import { StandardResponse } from './models/standard-response';
import { IUserData as IUserProfileData } from './models/profile';
import { checkEmailAlreadyExists, getTermAndCondition, signup } from './services/login/loginApi-service';
import ReferralPromotionPreference from './pages/referral-promotion-preferences/ReferralPromotionPreference';
import CookieConsent from 'react-cookie-consent';
import { IGetConfigurationData } from './models/profile/index';
import OpenSource from './pages/open-source/OpenSource';
import PaymentPage from './pages/payment/PaymentPage';
import { store } from './store';
import AboutUs from './pages/about-us/AboutUs';
import Referral from './pages/referral/Referral';
import PartnerPromotionPage from './pages/partnerPromotion/PartnerPromotionPage';
import DownloadVATReceipt from './pages/downloadVATReceipt/DownloadVATReceipt';
import { companyNameReplace } from './utils';
import { ISupportedLanguage } from './store/state/language.state';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { IListenerData, IObjectRequest, ITermsConditionData } from './models/login';
import type { IModelState, ITermsConditionModalState } from './types/stateTypes';
import ConfirmationBox from './components/common/ConfirmationBox';
import DOMPurify from 'isomorphic-dompurify';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { migrateAccountToThirdParty } from './services/federated-account-migration';
import PageSpinner from './components/shared/PageSpinner';
import OTPVerification from './pages/federated-account-migration/OTPVerification';
import ManageAccess from './pages/federated-account-migration/ManageAccess';
import MigrationSuccess from './pages/federated-account-migration/MigrationSuccess';
import AuthLayout from './layout/AuthLayout';
import AuthOptions from './pages/AuthSetup/AuthOptions';
import AuthAppSetup from './pages/AuthSetup/AuthAppSetup';
import AuthAppSetupOTP from './pages/AuthSetup/AuthAppSetupOTP';
import { getAuthOptions } from './services/auth-setup/auth-setup-service';
import TermsAndConditionModal from './pages/terms-conditions/TermsAndConditionModal';

import { ICommonErrorResponse } from 'models/common-model';
const RefundRequestGrid = React.lazy(() => import('./pages/refund-request/RefundRequestGrid'));
const ProfilePage = React.lazy(() => import('./pages/profile/profilePage'));
const SecurityPage = React.lazy(() => import('./pages/security/SecurityPage'));
const ReverifyEmail = React.lazy(() => import('./pages/security/ReverifyEmail'));
const HistoryPage = React.lazy(() => import('./pages/history/HistoryPage'));
const Payment = React.lazy(() => import('./pages/payment/Payment'));
const LoyaltyPointGrid = React.lazy(() => import('./pages/loyalty-points/LoyaltyPointGrid'));
const RFIDCardsPage = React.lazy(() => import('./pages/rfid-cards/RFIDCardsPage'));
const AuthNumberSetup = React.lazy(() => import('./pages/AuthSetup/AuthNumberSetup'));
const AuthNumberSetupOTP = React.lazy(() => import('./pages/AuthSetup/AuthNumberSetupOTP'));

Amplify.configure(amplifyConfig);

const queryStringLang = new URLSearchParams(window.location.search);
const languageInfo = store?.getState()?.LanguageInfo?.supportedLanguages;
const changelanguage = languageInfo
  ? languageInfo?.filter(
      (item: ISupportedLanguage) =>
        item?.code === queryStringLang.get('langCode') || item?.code === queryStringLang.get('language'),
    )[0]
  : {
      code: queryStringLang.get('langCode') || queryStringLang.get('language'),
    };

const App = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const actions = bindActionCreators(AppActionCreators, dispatch);

  const userData = useSelector((state: AppState) => state.UserData);
  const navigate = useNavigate();

  const location = useLocation();

  const storeState = store.getState();

  const isFederatedAccountMigrationPath = (pathname: string): boolean => {
    return (
      pathname.includes('/federated-accounts/migration/verify-otp') ||
      pathname.includes('/federated-accounts/migration/manage-access') ||
      pathname.includes('/federated-accounts/migration/success')
    );
  };

  const isInSignupFlow = () => {
    return sessionStorage.getItem(Common.SIGNUP_FLOW_ACTIVE) === 'true';
  };

  const clearSignupFlow = () => {
    sessionStorage.removeItem(Common.SIGNUP_FLOW_ACTIVE);
    sessionStorage.removeItem(Common.SIGNUP_CREDENTIALS);
  };

  const isAuthMethodSetupPath = (pathname: string): boolean => {
    return (
      pathname.includes('/auth/select-auth-option') ||
      pathname.includes('/auth/setup/authenticator') ||
      pathname.includes('/auth/setup/authenticator/OTP')
    );
  };

  const [isReady, setIsReady] = useState<boolean>(
    location.pathname.includes('/partner-promotion') ||
      location.pathname.includes('/payment/initiate') ||
      isFederatedAccountMigrationPath(location.pathname),
  );
  const [cookiePolicyURL, setCookiePolicyURL] = useState<string>('');
  const [modal, setModal] = useState<IModelState>({
    showModel: false,
    callBack: null,
    header: '',
    body: '',
  });

  const [showCookies, setShowCookies] = useState<boolean>(false);

  const [siteKeyV3, setSiteKeyV3] = useState<string>('');

  const [termsAndConditionModal, setTermsAndConditionModal] = useState<ITermsConditionModalState>({
    showModal: false,
    callBack: null,
    body: '',
  });

  const getTermsAndConditions = async () => {
    let response: StandardResponse<ITermsConditionData> | null = null;
    try {
      response = await getTermAndCondition();
    } catch (error) {
      if (error.response?.message) {
        toast.error(error.response.message);
      } else {
        toast.error(error.toString());
      }
    }
    return response;
  };

  const acceptTermsAndConditions = async (objRequest, user, termsAndConditionId) => {
    handleTermsAndConditionModal('', null, false);
    objRequest.term_and_condition = {
      accepted: true,
      id: termsAndConditionId,
    };
    await signup(objRequest);
    await getProfileData(user);
  };

  useEffect(() => {
    getV3RecaptchaSiteKey();
  }, []);

  const getV3RecaptchaSiteKey = async () => {
    const config = await getAPIKey({
      code: Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY_V3,
    });
    const siteKey = config.data.find(
      (item) => item.reference_code === Common.ConfigurationKey.GOOGLE_CAPTCHA_SITE_KEY_V3,
    )?.value;
    setSiteKeyV3(siteKey ?? '');
  };

  useEffect(() => {
    if (localStorage.getItem(Common.OauthFedratedSignIn)) {
      toast.error(t('login.toast.cancelled'));
      localStorage.removeItem(Common.OauthFedratedSignIn);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/download-receipt')) {
      sessionStorage.setItem('vat_receipt_path', location.pathname);
    }
  }, [location.pathname]);

  const setMigrationFlow = (isActive: boolean) => {
    if (isActive) {
      sessionStorage.setItem(Common.MigrationFlowKey, 'true');
    } else {
      sessionStorage.removeItem(Common.MigrationFlowKey);
    }
  };

  const isMigrationFlow = () => {
    return !!sessionStorage.getItem(Common.FederatedAccountMigrationSessionKeys.MIGRATION_TOKEN);
  };

  const getProfileData = async (user: AuthUser) => {
    let isMFARequired = false;
    try {
      const response = await getAuthOptions();
      isMFARequired = response.Data.driver2FAStatus.every((method) => !method.is_enabled);
    } catch (error) {
      isMFARequired = false;
    }
    const downloadVATRedirect = sessionStorage.getItem('vat_receipt_path');
    const userSession = await fetchAuthSession();
    document.getElementById('loading')?.classList.remove('manual-loading');
    await getProfile()
      .then((res: StandardResponse<IUserProfileData>) => {
        if (res && res.Data && res.Data.status && res.Data.status.code === Common.UserStatus.CANCELLED) {
          actions
            .logout()(dispatch)
            .then(() => {
              localStorage.setItem(Common.OauthFedratedSignIn, 'true');
              setIsReady(true);
            });
        } else {
          if (queryStringLang.get('langCode') || queryStringLang.get('language')) {
            getLanguageThroughQueryparams();
          } else {
            actions.changeLanguage(res?.Data?.language_preference);
          }
          actions.login({ ...user, ...userSession });
          actions.userDetail(res.Data);
          actions.setCurrencyDetails(res?.Data?.default_currency);
          setIsReady(true);

          if (!res.Data.is_federated) {
            if (isMFARequired) {
              sessionStorage.setItem(Common.SIGNUP_FLOW_ACTIVE, 'true');
            } else {
              sessionStorage.removeItem(Common.SIGNUP_FLOW_ACTIVE);
            }
          }

          if (downloadVATRedirect && downloadVATRedirect !== '') {
            sessionStorage.removeItem('vat_receipt_path');
            navigate(downloadVATRedirect);
            return;
          }
          if (localStorage.getItem(Common.SHOW_REFERRAL_SCREEN) === 'true') {
            const referralCode = localStorage.getItem('referralCode');
            navigate(Common.Routes.REFERRAL_PROMOTION_PREFERENCE, {
              state: { referralCode: referralCode },
              replace: true,
            });
            return;
          }

          if (!res?.Data.is_profile_verified) {
            if (location.pathname !== Common.PaymentPage && location.pathname !== Common.PaymentSecure) {
              navigate(Common.Routes.PROFILE_REDIRECT);
              return;
            }
          }

          if (res?.Data.payment_type === null) {
            if (location.pathname !== Common.PaymentPage && location.pathname !== Common.PaymentSecure) {
              navigate(Common.Routes.PAYMENT);
              return;
            }
          }

          navigate(Common.Routes.HOME);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        document.getElementById('loading')?.classList.add('spinner-disable');
      });
  };

  const fetchData = async () => {
    if (!(location.pathname.includes('/partner-promotion') || location.pathname.includes('/payment/initiate'))) {
      Hub.listen('auth', listener);
      await tryAutoSignIn();
      await getCookiePolicyURL();
    }
    if (!isFederatedAccountMigrationPath(location.pathname)) {
      setMigrationFlow(false);
    }
    await getLanguageThroughQueryparams();
    const config = await getAPIKey({ code: 'COMPANY_NAME' });
    actions.setCompanyName(config.data[0].value);
  };

  const getLanguageThroughQueryparams = async () => {
    if (queryStringLang.get('langCode') || queryStringLang.get('language')) {
      actions.changeLanguage(changelanguage);
    }
  };

  const listener = async (data: IListenerData) => {
    switch (data.payload.event) {
      case 'signInWithRedirect': {
        const user: AuthUser = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();

        const storedTermsAndCondition = localStorage.getItem('terms_and_condition');
        let termsAndCondition = null;

        if (storedTermsAndCondition && storedTermsAndCondition !== 'undefined') {
          try {
            termsAndCondition = JSON.parse(storedTermsAndCondition);
          } catch {
            termsAndCondition = null;
          }
        }

        localStorage.removeItem('terms_and_condition');

        if (isMigrationFlow()) {
          await handleMigrationFlow(user, await fetchAuthSession());
          return;
        }

        const federated_account_type = JSON.parse(userAttributes.identities!)[0].providerName;

        try {
          const name = userAttributes?.name?.split(' ');
          const objRequest = {
            first_name: name && name[0] ? name[0] : ' ',
            last_name: name && name[1] ? name[1] : ' ',
            email: userAttributes?.email,
            provider_key: userAttributes?.sub,
            federated_account_type: federated_account_type == 'SignInWithApple' ? 'Apple' : federated_account_type,
            is_federated: true,
            language_preference_id: storeState?.UserData?.language?.id || storeState?.LanguageInfo?.defaultLanguage?.id,
            term_and_condition: termsAndCondition,
          };

          checkEmailAlreadyExistsOrNot(objRequest, user);
        } catch (_err) {}

        break;
      }
      case 'signedIn_failure':
        console.warn('user sign in failed');
        break;
      case 'configured':
        console.warn('the Auth module is configured');
        break;
      default:
        console.warn('Nothing Happened');
    }
  };

  const handleUserProfileAndNavigation = async (currentUser: AuthUser, userSession: AuthSession) => {
    let isMFARequired = false;
    try {
      const response = await getAuthOptions();
      isMFARequired = response.Data.driver2FAStatus.every((method) => !method.is_enabled);
    } catch (error) {
      isMFARequired = false;
    }
    document.getElementById('loading')?.classList.remove('spinner-disable');
    await getProfile()
      .then((userDetail: StandardResponse<IUserProfileData>) => {
        if (
          userDetail &&
          userDetail.Data &&
          userDetail.Data.status &&
          userDetail.Data.status.code === Common.UserStatus.CANCELLED
        ) {
          actions
            .logout()(dispatch)
            .then(() => {
              localStorage.setItem(Common.OauthFedratedSignIn, 'true');
              setIsReady(true);
            });
        } else {
          if (queryStringLang.get('langCode') || queryStringLang.get('language')) {
            getLanguageThroughQueryparams();
          } else {
            actions.changeLanguage(userDetail?.Data?.language_preference);
          }
          actions.login({ ...currentUser, ...userSession });
          actions.userDetail(userDetail.Data);
          actions.setCurrencyDetails(userDetail?.Data?.default_currency);
          setIsReady(true);

          if (!userDetail.Data.is_federated) {
            if (isMFARequired) {
              sessionStorage.setItem(Common.SIGNUP_FLOW_ACTIVE, 'true');
            } else {
              sessionStorage.removeItem(Common.SIGNUP_FLOW_ACTIVE);
            }
          }

          if (!location?.pathname?.includes('/download-receipt')) {
            if (isFederatedAccountMigrationPath(location?.pathname || '')) {
              return;
            }

            if (isAuthMethodSetupPath(location?.pathname || '') && isInSignupFlow()) {
              return;
            }

            if (sessionStorage.getItem(Common.SIGNUP_FLOW_ACTIVE) === 'true') {
              navigate('/auth/select-auth-option', { replace: true });
              return;
            }

            clearSignupFlow();

            if (localStorage.getItem(Common.SHOW_REFERRAL_SCREEN) === 'true') {
              const referralCode = localStorage.getItem('referralCode');
              navigate('/referral-promotion-preference', {
                state: { referralCode: referralCode },
                replace: true,
              });
              return;
            }
            if (userDetail?.Data.is_profile_verified === false) {
              if (location.pathname !== Common.PaymentPage && location.pathname !== Common.PaymentSecure) {
                navigate(Common.Routes.PROFILE_REDIRECT);
                return;
              }
            }

            if (userDetail?.Data.payment_type === null) {
              if (location.pathname !== Common.PaymentPage && location.pathname !== Common.PaymentSecure) {
                navigate(Common.Routes.PAYMENT);
              }
            }
          }
        }
      })
      .catch(() => {
        const myReferralCode = new URLSearchParams(window.location.search).get('code');
        if (!(myReferralCode && window.location.pathname === '/')) {
          actions.logout();
          toast.error(t('login.toast.error'));
        }
        setIsReady(true);
      })
      .finally(() => {
        document.getElementById('loading')?.classList.add('spinner-disable');
      });
  };

  const continueNormalAuthFlow = async (currentUser: AuthUser, userSession: AuthSession) => {
    await handleUserProfileAndNavigation(currentUser, userSession);
  };

  const handleMigrationFlow = async (currentUser: AuthUser, userSession: AuthSession) => {
    const migrationToken = sessionStorage.getItem(Common.FederatedAccountMigrationSessionKeys.MIGRATION_TOKEN) || '';
    const clearSessionAndNavigateToManageAccess = () => {
      for (const key in localStorage) {
        if (key.includes('CognitoIdentityServiceProvider')) {
          localStorage.removeItem(key);
        }
      }
      setMigrationFlow(false);
      const langCode = storeState?.UserData?.language?.code || storeState?.LanguageInfo?.defaultLanguage?.code || 'en';
      const exp = Date.now() + 10 * 60 * 1000;
      sessionStorage.setItem(
        `${Common.FederatedAccountMigrationSessionKeys.MIGRATION_OTP_VERIFIED}:${migrationToken}`,
        JSON.stringify({ ok: true, exp }),
      );
      navigate(`/federated-accounts/migration/manage-access/${migrationToken}?lang=${langCode}`, {
        replace: true,
      });
    };
    try {
      const cognitoUsername = currentUser.username;
      const migrationResult = await migrateAccountToThirdParty(migrationToken, cognitoUsername);

      if (migrationResult.Status === 200) {
        setMigrationFlow(false);
        sessionStorage.removeItem(Common.FederatedAccountMigrationSessionKeys.MIGRATION_TOKEN);
        await continueNormalAuthFlow(currentUser, userSession);
      } else {
        clearSessionAndNavigateToManageAccess();
      }
    } catch (error) {
      const errMsg = (error as ICommonErrorResponse)?.response?.data?.message || String(error);
      toast.error(errMsg);
      clearSessionAndNavigateToManageAccess();
    }
  };

  const tryAutoSignIn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isFederate = localStorage.getItem('is_federate');
    const myReferralCode = urlParams.get('code');
    if (myReferralCode !== null && myReferralCode !== '' && !myReferralCode.includes('-')) {
      localStorage.setItem('referralCode', myReferralCode);
    }

    try {
      const currentUser: AuthUser = await getCurrentUser();
      const userSession: AuthSession = await fetchAuthSession();

      if (currentUser && userSession && isFederate !== 'true') {
        if (isMigrationFlow()) {
          await handleMigrationFlow(currentUser, userSession);
          return;
        }
        await handleUserProfileAndNavigation(currentUser, userSession);
      } else {
        localStorage.removeItem('is_federate');
      }
    } catch (_error) {
      if (!(myReferralCode && window.location.pathname === '/')) {
        actions.logout();
      }
      setIsReady(true);
    }
  };

  const getCookiePolicyURL = async () => {
    await getConfigurationDetail({
      code: Common.ConfigurationKey.COOKIE_POLICY_URL,
    })
      .then((res: IGetConfigurationData) => {
        setCookiePolicyURL(res.data[0]?.value);
      })
      .catch((e) => {
        if (e.response && e.response.data && e.response.data.message) {
          toast.error(e.response.data.message);
        } else {
          toast.error(e.toString());
        }
      });
  };

  const handleModal = (body: string, header: string, callBack: () => void) => {
    setModal((prevModal) => ({
      body: body,
      header: header,
      callBack: callBack,
      showModel: !prevModal.showModel,
    }));
  };

  const closeModal = () => {
    setModal({
      body: '',
      header: '',
      callBack: null,
      showModel: false,
    });

    actions.logout();
    navigate('/login');
  };

  const checkAuthSetupAccess = (component: React.ReactNode) => {
    if (
      !(userData && userData.authConfigs && userData.authConfigs.accessToken) ||
      userData.authConfigs.accessToken === ''
    ) {
      return <Navigate replace to="/login" />;
    }

    if (!isInSignupFlow()) {
      return <Navigate replace to="/login" />;
    }

    return component;
  };

  const handleTermsAndConditionModal = (body: string, callBack, showModal) => {
    setTermsAndConditionModal({
      body: body,
      callBack: callBack,
      showModal: showModal,
    });
  };

  const checkEmailAlreadyExistsOrNot = async (objRequest: IObjectRequest, user: AuthUser) => {
    const logTypeMap = {
      Google: 1,
      Apple: 2,
      Facebook: 3,
    };
    const logType = logTypeMap[objRequest.federated_account_type as string];

    const input = {
      email: objRequest.email,
      log_type: logType,
    };

    try {
      const response = await checkEmailAlreadyExists(input);

      if (response.Data.log_added) {
        const response = await getTermsAndConditions();
        objRequest.term_and_condition = {
          accepted: true,
          id: Number(response?.Data.id),
        };
        await signup(objRequest);
        await getProfileData(user);
        return;
      }

      localStorage.setItem(Common.SHOW_REFERRAL_SCREEN, 'true');

      if (response.Data.type_of_log.length === 0) {
        if (response.Data.new_log_required) {
          if (objRequest.term_and_condition?.accepted) {
            await signup(objRequest);
            await getProfileData(user);
            return;
          }
          const response = await getTermsAndConditions();
          handleTermsAndConditionModal(
            String(response?.Data?.content),
            () => acceptTermsAndConditions(objRequest, user, response?.Data?.id),
            true,
          );
          return;
        }
        await signup(objRequest);
        await getProfileData(user);
        return;
      }

      const accountTypeMap: Record<string, string | null> = {
        1: 'Google',
        2: 'Apple',
        3: 'Facebook',
        4: null,
      };

      const existingEmails = response.Data.type_of_log.map((type: number) => ({
        account_type: accountTypeMap[type],
      }));

      let modalMessage = '';
      modalMessage = companyNameReplace(t('signup.label.modalMessage'));
      const emailString = `<span class='themeTextColor'>${String(objRequest.email).toString()}</span>`;
      modalMessage = modalMessage.replace('##EMAIL##', emailString);

      const accounts = existingEmails
        .map(({ account_type }) => {
          const displayName = account_type ?? companyNameReplace(t('signup.label.instavoltUserAccount'));
          return `<span class='themeTextColor'>${displayName}</span>`;
        })
        .join(', ');

      modalMessage = modalMessage.replace('##FEDERATEDACCOUNT##', accounts);
      handleModal(modalMessage, companyNameReplace(t('signup.label.instavolt')), () =>
        signUpFederatedAccount(objRequest, user, response),
      );
    } catch (error) {
      if (error.response?.data?.message) {
        sessionStorage.setItem('error_message', error.response.data.message);
        actions.logout();
      } else {
        toast.error(error.toString());
      }
    }
  };

  const signUpFederatedAccount = async (objRequest: IObjectRequest, user: AuthUser, response) => {
    if (response.Data.new_log_required) {
      if (objRequest.term_and_condition?.accepted) {
        await signup(objRequest);
        await getProfileData(user);

        setModal({
          body: '',
          header: '',
          callBack: null,
          showModel: false,
        });
        return;
      }
      const termsAndConditionResponse = await getTermsAndConditions();
      handleTermsAndConditionModal(
        String(termsAndConditionResponse?.Data?.content),
        () => acceptTermsAndConditions(objRequest, user, termsAndConditionResponse?.Data?.id),
        true,
      );

      setModal({
        body: '',
        header: '',
        callBack: null,
        showModel: false,
      });
      return;
    }

    await signup(objRequest);
    await getProfileData(user);
  };

  const handleLanguageAndError = () => {
    if (!showCookies) {
      setShowCookies(true);
    }
  };

  const checkAuthenticatedUser = (component) => {
    if (isFederatedAccountMigrationPath(location.pathname)) {
      return component;
    }

    if (location.pathname.includes('/referral-promotion-preference')) {
      if (localStorage.getItem(Common.SHOW_REFERRAL_SCREEN) === 'true') {
        return component;
      } else {
        return <Navigate replace to="/login" />;
      }
    }

    if (
      !(userData && userData.authConfigs && userData.authConfigs.accessToken) ||
      userData.authConfigs.accessToken === ''
    ) {
      return <Navigate replace to="/login" />;
    } else {
      return component;
    }
  };

  const checkNotAuthenticatedUser = (isSignUp: boolean) => {
    if (sessionStorage.getItem(Common.OTP_VERIFICATION_IN_PROGRESS) === 'true') {
      document.getElementById('loading')?.classList.remove('spinner-disable');
      return null;
    }
    document.getElementById('loading')?.classList.add('spinner-disable');
    if (userData === null || (userData && userData.language && !userData.authConfigs)) {
      if (isSignUp) {
        return <LoginPage isSignUp={isSignUp} handleLanguageAndError={handleLanguageAndError} key={location.key} />;
      } else {
        return <LoginPage handleLanguageAndError={handleLanguageAndError} key={location.key} />;
      }
    } else if (isInSignupFlow()) {
      return <Navigate replace to="/auth/select-auth-option" />;
    } else if (localStorage.getItem(Common.SHOW_REFERRAL_SCREEN) === 'true') {
      return <Navigate replace to="/referral-promotion-preference" />;
    } else if (userData.is_profile_verified === false) {
      return <Navigate replace to="/profile?redirect=true" />;
    } else if (userData.payment_type === null) {
      return <Navigate replace to="/payment" />;
    } else {
      return <Navigate replace to="/" />;
    }
  };

  return (
    <>
      {siteKeyV3 && (
        <GoogleReCaptchaProvider reCaptchaKey={siteKeyV3} useEnterprise={true}>
          <Suspense fallback={<PageSpinner />}>
            <Routes>
              <Route path="/federated-accounts/migration/verify-otp/:token" element={<OTPVerification />} />
              <Route path="/federated-accounts/migration/manage-access/:token" element={<ManageAccess />} />
              <Route path="/federated-accounts/migration/success" element={<MigrationSuccess />} />
              <Route path="/payment/initiate/:platform?/:version?" element={<PaymentPage />} />
              {isReady && (
                <>
                  <Route path="/FAQ" element={<FAQPage />} />
                  <Route path="/fleet-faq" element={<FleetFAQPage />} />
                  <Route path="/open-source" element={<OpenSource />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/premium-subscription" element={<PremiumSubscriptionPage />} />

                  <Route path="/referral/:id?" element={<Referral />} />
                  <Route path="/partner-promotion/:id/:driver_id/:session_id?" element={<PartnerPromotionPage />} />
                  <Route path="/login" element={checkNotAuthenticatedUser(false)} />
                  <Route path="/sign-up" element={checkNotAuthenticatedUser(true)} />
                  <Route
                    path="/referral-promotion-preference"
                    element={checkAuthenticatedUser(<ReferralPromotionPreference />)}
                  />
                  <Route
                    path="/download-receipt/:session_id?/:session_primary_id?"
                    element={checkAuthenticatedUser(<DownloadVATReceipt />)}
                  />
                  <Route element={<MainLayout logout={actions.logout} />}>
                    <Route path="/" element={checkAuthenticatedUser(<HistoryPage />)} />
                    <Route path="/Profile" element={checkAuthenticatedUser(<ProfilePage />)} />
                    <Route path="/security" element={checkAuthenticatedUser(<SecurityPage />)} />
                    <Route path="/payment" element={checkAuthenticatedUser(<Payment />)} />
                    <Route path="/rewards" element={checkAuthenticatedUser(<LoyaltyPointGrid />)} />
                    <Route path="/refundRequests" element={checkAuthenticatedUser(<RefundRequestGrid />)} />
                    <Route path="/rfid-cards" element={checkAuthenticatedUser(<RFIDCardsPage />)} />
                  </Route>
                  <Route element={<AuthLayout />}>
                    <Route path="/reverify-email-otp" element={checkAuthenticatedUser(<ReverifyEmail />)} />
                  </Route>
                  <Route element={<AuthLayout />}>
                    <Route path="/auth/select-auth-option" element={checkAuthSetupAccess(<AuthOptions />)} />
                    <Route path="/auth/setup/authenticator" element={checkAuthSetupAccess(<AuthAppSetup />)} />
                    <Route path="/auth/setup/authenticator/OTP" element={checkAuthSetupAccess(<AuthAppSetupOTP />)} />
                    <Route path="/auth/setup/phone-number" element={checkAuthSetupAccess(<AuthNumberSetup />)} />
                    <Route path="/auth/setup/phone-number/OTP" element={checkAuthSetupAccess(<AuthNumberSetupOTP />)} />
                  </Route>
                  {/* <Redirect to="/login" /> */}
                  <Route path="/" element={<Navigate replace to="/login" />} />
                  <Route path="*" element={<Navigate to="/login" />} />
                </>
              )}
            </Routes>
          </Suspense>
        </GoogleReCaptchaProvider>
      )}
      <ToastContainer />
      {!(
        location.pathname.toLowerCase().includes('faq') ||
        location.pathname.toLowerCase().includes('open-source') ||
        location.pathname.toLowerCase().includes('premium-subscription') ||
        location.pathname.toLowerCase().includes('partner-promotion') ||
        location.pathname.toLowerCase().includes('/payment/initiate') ||
        location.pathname.toLowerCase().includes('/about-us')
      ) && (
        <CookieConsent buttonText={t('cookiePolicy.iAccept')} expires={30}>
          {t('cookiePolicy.text')}{' '}
          <a href={cookiePolicyURL} target="_blank" rel="noopener noreferrer" className="cookiePolicy">
            {t('cookiePolicy.action')}
          </a>
        </CookieConsent>
      )}
      {modal.showModel && (
        <ConfirmationBox
          onCancel={closeModal}
          onConfirm={modal.callBack}
          header={modal.header}
          bodyMessage={
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(modal.body),
              }}
            ></div>
          }
          Message={modal.body}
          body={modal.body}
          showModel={modal.showModel}
          closeModel={closeModal}
        />
      )}

      <TermsAndConditionModal modal={termsAndConditionModal} isShowCloseButton={false} isShowAccceptButton={true} />
    </>
  );
};

export default App;
